import React, { useCallback } from "react";
import { FinancialDocument } from "@common/types/financialDocument";
import { useRemoveFinancialDocumentMutation } from "@api/financialDocument.api";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { DetachIcon, TrashIcon } from "@icons";
import styles from "./AttachedFinancialDocumentRow.module.scss";

interface Props {
    financialDocument: FinancialDocument;
    detachDocument: (documentId: number) => void;
    onViewDocument: () => void;
    detachAriaLabel: string;
    detachTooltip: string;
    testId: string;
    deleteAction: "deleteOnly" | "detachThenDelete";
}

export const AttachedFinancialDocumentRow: React.FC<Props> = ({
    financialDocument,
    detachDocument,
    onViewDocument,
    detachAriaLabel,
    detachTooltip,
    testId,
    deleteAction,
}) => {
    const removeDocumentMutation = useRemoveFinancialDocumentMutation();
    const handleDetach = useCallback(() => {
        detachDocument(financialDocument.id);
    }, [detachDocument, financialDocument.id]);

    const handleDelete = useCallback(() => {
        if (deleteAction === "detachThenDelete") {
            handleDetach();
        }
        removeDocumentMutation.mutate({
            financialDocumentId: financialDocument.id,
        });
    }, [
        deleteAction,
        financialDocument.id,
        handleDetach,
        removeDocumentMutation,
    ]);

    return (
        <div className={styles.match} data-testid={testId}>
            <Button
                variant="secondary"
                size="sm"
                className={styles.documentName}
                onClick={onViewDocument}
            >
                <span>{financialDocument.fileName}</span>
            </Button>

            <Tooltip content={detachTooltip}>
                <Button
                    data-testid="detach-document-button"
                    variant="secondary"
                    iconOnly
                    size="sm"
                    onClick={handleDetach}
                    aria-label={detachAriaLabel}
                >
                    <DetachIcon />
                </Button>
            </Tooltip>

            <ButtonWithLoader
                data-testid="remove-document-button"
                variant="secondary"
                iconOnly
                size="sm"
                aria-label="Delete document"
                tooltip="Delete document"
                loading={removeDocumentMutation.isPending}
                onClick={handleDelete}
            >
                <TrashIcon />
            </ButtonWithLoader>
        </div>
    );
};
